import { API_URL } from "../../config";
import { getApiToken } from "../../api-token";
import { loadPuzzelQueues, loadPuzzelToken, loadPuzzelQueueUserDetails } from './puzzel-queue';

export const LAYOUTS = {
    "dashboard": "Dashboard",
    "status-overview": "Status overview",
    "table": "Table",
};

export async function listWallboards(continuation_token) {
    try {
        const [loggedIn, token] = getApiToken();
        if (!loggedIn)
            return [false, null, null, "Not logged in"];

        let url = `${API_URL}/api/list-wallboards`;
        if (continuation_token)
            url += `?continuationToken=${continuation_token}`

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Authorization": token,
            },
        });

        if (response.status !== 200) {
            if (response.status === 500) {
                return [false, null, null, "Internal error"];
            }
            if (response.status === 401) {
                return [false, null, null, "Unauthorized"];
            }

            const error = await response.text();
            return [false, null, null, error];
        }

        const { wallboards, continuationToken } = await response.json();
        return [true, wallboards, continuationToken, null];
    }
    catch {
        return [false, null, null, "Failed to load wallboards"];
    }
}

export async function loadWallboard(id) {
    try {
        const [loggedIn, token] = getApiToken();
        if (!loggedIn)
            return [false, null, "Not logged in"];

        const response = await fetch(`${API_URL}/api/load-wallboard/${id}`, {
            method: "GET",
            headers: {
                "Authorization": token,
            },
        });

        if (response.status !== 200) {
            const error = await response.text();
            return [false, null, error];
        }

        const wallboard = await response.json();
        return [true, wallboard, null];
    }
    catch {
        return [false, null, "Failed to load wallboard"];
    }
}

export async function loadWallboardQueues(customerKeys, tokens, setTokens) {
    const queues = [];

    for (const customerKey of customerKeys) {
        const [tokenLoaded, token] = await getOrReloadPuzzelToken(tokens, customerKey.customerKey, setTokens);
        if (!tokenLoaded) {
            continue;
        }

        const [loaded, _queues, resetToken,] = await loadPuzzelQueues(token, customerKey.customerKey, customerKey.filterIds);
        if (!loaded) {
            if (resetToken) {
                tokens[customerKey.customerKey] = null;
                setTokens(tokens);
            }
            continue;
        }

        for (const queue of _queues) {
            queues.push(queue);
        }
    }

    return queues;
}

async function getOrReloadPuzzelToken(tokens, customerKey, setTokens) {
    const token = tokens[customerKey];
    if (token)
        return [true, token];

    const [tokenLoaded, newToken,] = await loadPuzzelToken(customerKey);
    if (!tokenLoaded) {
        return [false, null];
    }

    tokens[customerKey] = newToken;
    setTokens(tokens);
    return [true, newToken];
}

export async function loadWallboardAgentsForQueues(customerKeys, tokens, setTokens) {
    let loggedOn = new Set();
    let available = new Set();
    let connected = new Set();
    let unavailable = new Set();
    let pause = new Set();

    for (const customerKey of customerKeys) {
        const [tokenLoaded, token] = await getOrReloadPuzzelToken(tokens, customerKey.customerKey, setTokens);
        if (!tokenLoaded) {
            continue;
        }

        const queueIds = customerKey.filterIds.split(",").filter(id => id !== "");
        for (const queueId of queueIds) {
            const [loaded, data, resetToken,] = await loadPuzzelQueueUserDetails(token, customerKey.customerKey, queueId);
            if (!loaded) {
                if (resetToken) {
                    tokens[customerKey.customerKey] = null;
                    setTokens(tokens);
                }
                continue;
            }

            const usersDetails = data.usersDetails;
            for (const user of usersDetails) {
                if (!user.activeOnQueue) {
                    continue;
                }

                if (user.contactCentreStatus === "Pause") {
                    pause.add(user.userId);
                }
                if (user.contactCentreStatus === "LoggedOn") {
                    loggedOn.add(user.userId);

                    if (user.userStatus === "Available") {
                        available.add(user.userId);
                    }
                    else if (user.userStatus === "Connected") {
                        connected.add(user.userId);
                    }
                    else {
                        unavailable.add(user.userId);
                    }
                }
            }
        }
    }

    return [loggedOn, available, connected, unavailable, pause];
}

export async function loadWallboardAgentDetailsForQueues(customerKeys, tokens, setTokens) {
    let available = {};
    let connected = {};
    let unavailable = {};
    let pause = {};

    for (const customerKey of customerKeys) {
        const [tokenLoaded, token] = await getOrReloadPuzzelToken(tokens, customerKey.customerKey, setTokens);
        if (!tokenLoaded) {
            continue;
        }

        const queueIds = customerKey.filterIds.split(",").filter(id => id !== "");
        for (const queueId of queueIds) {
            const [loaded, data, resetToken,] = await loadPuzzelQueueUserDetails(token, customerKey.customerKey, queueId);
            if (!loaded) {
                if (resetToken) {
                    tokens[customerKey.customerKey] = null;
                    setTokens(tokens);
                }
                continue;
            }

            for (const user of data.usersDetails) {
                if (!user.activeOnQueue) {
                    continue;
                }

                if (user.contactCentreStatus === "Pause") {
                    pause[user.userId] = user;
                }
                if (user.contactCentreStatus === "LoggedOn") {
                    if (user.userStatus === "Available") {
                        available[user.userId] = user;
                    }
                    else if (user.userStatus === "Connected") {
                        connected[user.userId] = user;
                    }
                    else {
                        unavailable[user.userId] = user;
                    }
                }
            }
        }
    }

    return [available, connected, unavailable, pause];
}